/**
 * AutoComplete Component
 *
 * This component is a wrapper around Material-UI's Autocomplete, providing additional customization and
 * functionality.
 * It supports dynamic filtering, custom rendering of input and options, and various styling options.
 *
 * */
import React, { useRef, useState, useCallback } from 'react';
import Autocomplete, {
    createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import Popper from '@material-ui/core/Popper';
import {
    InputContainer,
    useStyles,
    AutocompleteDiv,
} from './AutoComplete.styled';
import { useTranslation } from 'react-i18next';
import { sizes } from 'src/styles/Media.styled';
import InputField from './InputField';
import Search from 'src/assets/images/Search.svg';
import DownIcon from './DownIcon.jsx';
import {
    PRIMARY_FONT_COLOR, TERTIARY_FONT_COLOR
} from '../../../../styles/fonts/MCFontConstants.js';
import { logErrorToConsole } from 'src/utils/util';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';

export default function AutoComplete(props) {
    const {
        isPhysician,
        filterField,
        field,
        mbBorderRadius,
        paddingLeft,
        fontSize,
        border,
        optionPadding,
        SearchGrayInactive,
        value,
        mbPaddingLeft,
        zIndex,
        hoverBgColor,
        marginLeft,
        dropdownHeight,
        quickOrderPadding,
        closeIconDisplay,
        fontSizePlaceholder,
        marginLeftOption,
        mbWidth,
        isPhysicianField,
        width,
        surgeryShipTo,
        id,
        options,
        optionValue,
        itemTemplate,
        onChange,
        style,
        showScrollBar,
        detailsMargin,
        showSearchIcon = false,
        inputValue = '',
        disabled,
        placeholder,
        autoCompleteInputFieldStyle,
        inputRef,
        onOpen,
        open,
        onClose,
        loading,
        onFocus,
        noOptionsText,
        onInputChange,
        onKeyPress,
        onBlur,
        searchIconHeight,
        searchIconWidth,
        isFromShiptoAddress,
        height,
        isFromPhysician,
        isFromSurgery,
        isFromMazorCase,
        isMobile,
        disableClearable,
        hideNoOptionText,
        // isPhysician,
        readOnly,
        clearOnBlur,
        customWidth,
        isAddItemSearchItem,
        isProductSearch,
        forcePopupIcon = true,
        changeBackgroundDisabled,
        isQuickOrder,
        listBoxProps,
        isAddProxy = false,
        isSearchDropdown,
        disableCloseOnSelect,
        isBulkAddSearch = false,
        closeOnAllowedReasons, 
        isEquity,
        key,
        getOptionDisabled,
        isBundleProduct,
        forceActiveColor
    } = props;

    const inputColor =
        readOnly || forceActiveColor ? PRIMARY_FONT_COLOR : TERTIARY_FONT_COLOR;
    const wrapperRef = useRef(null);
    const ignoreNextPopupBlurRef = useRef(false);
    const lastPopupInteractionRef = useRef(0);
    const lastTouchStartRef = useRef(0);

    const WiderPopper = React.useCallback((popperProps) => {
        const { style, ...rest } = popperProps;
        const isAboveMobile = window.innerWidth > sizes.mobileDevice;
        const isIpad = window.innerWidth > sizes.mobileDevice && window.innerWidth <= sizes.mediumDevice;
        const multiplier = isAboveMobile ? 
                            ((isBundleProduct && isIpad) ? 1.4 : (isBundleProduct && !isIpad) ? 1.5 
                            : isAddItemSearchItem? 1.4: isBulkAddSearch ? 1.3 : 1.5) 
                            : ((isBulkAddSearch && isEquity) ? 1.5 : 1);
        const adjustedWidth = style?.width
            ? parseFloat(style.width) * multiplier
            : style?.width;
        return <Popper {...rest} style={{ ...style, width: adjustedWidth, 
            zIndex: isBundleProduct ? 99999 : 2500 }} placement="bottom-start" />;
    }, [isAddItemSearchItem, isBulkAddSearch, isEquity, isBundleProduct]);

    const filterOptions = createFilterOptions({
        trim: true,
        stringify: (option) => {
            if (!option) return '';
            return option[filterField] || option[field] || '';
        },
    });

    const classes = useStyles({
        mbBorderRadius: mbBorderRadius,
        paddingLeft: paddingLeft,
        fontSize: fontSize,
        border: border,
        optionPadding: optionPadding,
        SearchGrayInactive: SearchGrayInactive,
        value: value,
        mbPaddingLeft: mbPaddingLeft,
        zIndex: zIndex,
        hoverBgColor: hoverBgColor,
        marginLeft: marginLeft,
        dropdownHeight: dropdownHeight,
        quickOrderPadding: quickOrderPadding,
        closeIconDisplay: closeIconDisplay,
        fontSizePlaceholder: fontSizePlaceholder,
        marginLeftOption: marginLeftOption,
        showSearchIcon: showSearchIcon,
        showScrollBar: showScrollBar,
        inputColor: inputColor,
        isAddItemSearchItem: isAddItemSearchItem,
        isProductSearch: isProductSearch,
        isQuickOrder: isQuickOrder,
        readOnly: readOnly,
        changeBackgroundDisabled: changeBackgroundDisabled,
        isSearchDropdown:isSearchDropdown
    });

    const { t } = useTranslation();
    const isMobileDevice = window.innerWidth < sizes.mobileDevice;
    // Determine the width based on the device type and field type

    const handleClearAndFocus = () => {
        setTimeout(() => {
            try {
                inputRef.current.focus();
            }
            catch (error) {
                logErrorToConsole(error);
            }
        }, 100);
    };

    // Track if input is visible in viewport for product search
    const [isInputVisible, setIsInputVisible] = useState(true);

    useEffectWithErrorLogging(() => {
        if (!isProductSearch || !open || !wrapperRef.current) {
            setIsInputVisible(true);
            return;
        }

        const checkVisibility = () => {
            if (wrapperRef.current) {
                const rect = wrapperRef.current.getBoundingClientRect();
                const visible = rect.bottom > 0 && rect.top < window.innerHeight;
                setIsInputVisible(visible);
            }
        };

        checkVisibility();
        document.addEventListener('scroll', checkVisibility, true);
        return () => document.removeEventListener('scroll', checkVisibility, true);
    }, [isProductSearch, open]);

    const effectiveOpen = isProductSearch ? (open && isInputVisible) : open;

    const isPopupLikeElement = useCallback((element) => {
        if (!element || typeof element.closest !== 'function') {
            return false;
        }

        return Boolean(
            element.closest(
                '[role="dialog"], .MuiDialog-root, .MuiModal-root, .MuiPopover-root, [id*="popup"], [id*="popUp"], [id*="Popup"], [id*="modal"], [data-popup-container]'
            )
        );
    }, []);

    const handleAutocompleteClose = useCallback(
        (event, reason) => {
            if (!isSearchDropdown) {
                onClose && onClose(event, reason);
                return;
            }

            const focusTarget = event?.relatedTarget || document.activeElement;
            const hadRecentPopupInteraction =
                Date.now() - lastPopupInteractionRef.current < 250;

            if (
                reason === 'blur' &&
                (ignoreNextPopupBlurRef.current || hadRecentPopupInteraction)
            ) {
                ignoreNextPopupBlurRef.current = false;
                return;
            }

            if (
                reason === 'blur' &&
                isPopupLikeElement(focusTarget)
            ) {
                return;
            }

            // If blur was not preceded by a touchstart within 500ms, it means
            // no real screen tap happened — the blur came from the native keyboard
            // (Done/tick button, auto-suggested word tap). Block it.
            // On desktop (no touch support) this check is skipped so blur closes normally.
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const hadRecentTouch = Date.now() - lastTouchStartRef.current < 500;
            if (reason === 'blur' && isTouchDevice && !hadRecentTouch) {
                return;
            }

            if (
                Array.isArray(closeOnAllowedReasons) &&
                closeOnAllowedReasons.length > 0
            ) {
                const inputIsEmpty =
                    typeof inputValue === 'string'
                        ? inputValue.length === 0
                        : !inputValue;
                const isAllowedReason = closeOnAllowedReasons.includes(reason);
                const isEmptyInputClose =
                    closeOnAllowedReasons.includes('empty-input') && inputIsEmpty;

                if (!isAllowedReason && !isEmptyInputClose) {
                    return;
                }
            }

            onClose && onClose(event, reason);
        },
        [
            closeOnAllowedReasons,
            inputValue,
            isSearchDropdown,
            isPopupLikeElement,
            onClose,
        ]
    );

    useEffectWithErrorLogging(() => {
        if(!isSearchDropdown) return;

        if (!effectiveOpen || readOnly) {
            return undefined;
        }

        const handleDocumentMouseDownCapture = (event) => {
            const target = event?.target;

            if (!target) {
                return;
            }

            // For bundle products, check if click is inside the autocomplete/popper
            if (isBundleProduct) {
                const isInsideAutocomplete = wrapperRef.current?.contains(target);
                const isInsidePopper = 
                    typeof target.closest === 'function' &&
                    target.closest('.MuiAutocomplete-popper, .MuiAutocomplete-paper');
                
                // If click is outside autocomplete and popper, allow blur to close dropdown
                if (!isInsideAutocomplete && !isInsidePopper) {
                    ignoreNextPopupBlurRef.current = false;
                    return;
                }
            }

            const isPopupInteraction = isPopupLikeElement(target);

            if (isPopupInteraction) {
                // Only ignore blur if the popup is NOT a parent of the autocomplete wrapper
                // (i.e., it's a child popup spawned by an autocomplete action, not the outer dialog)
                const POPUP_SELECTOR = '[role="dialog"], .MuiDialog-root, .MuiModal-root, .MuiPopover-root, [id*="popup"], [id*="popUp"], [id*="Popup"], [id*="modal"], [data-popup-container]';
                const clickedPopup = typeof target.closest === 'function' && target.closest(POPUP_SELECTOR);
                const isChildPopup = clickedPopup && wrapperRef.current && !clickedPopup.contains(wrapperRef.current);
                ignoreNextPopupBlurRef.current = !!isChildPopup;
                if (isChildPopup) {
                    lastPopupInteractionRef.current = Date.now();
                }
            } else {
                ignoreNextPopupBlurRef.current = false;
            }
        };

        const handleDocumentTouchStartCapture = (event) => {
            const target = event?.target;

            if (!target) {
                return;
            }

            // Track every touch so we can distinguish keyboard blurs (no preceding
            // touch) from real tap-outside blurs (touchstart fires first).
            lastTouchStartRef.current = Date.now();

            // For bundle products, check if touch is inside the autocomplete/popper
            if (isBundleProduct) {
                const isInsideAutocomplete = wrapperRef.current?.contains(target);
                const isInsidePopper = 
                    typeof target.closest === 'function' &&
                    target.closest('.MuiAutocomplete-popper, .MuiAutocomplete-paper');
                
                // If touch is outside autocomplete and popper, allow blur to close dropdown
                if (!isInsideAutocomplete && !isInsidePopper) {
                    ignoreNextPopupBlurRef.current = false;
                    return;
                }
            }

            const isPopupInteraction = isPopupLikeElement(target);
            if (isPopupInteraction) {
                // Only ignore blur if the popup is a child popup (not the outer dialog containing the autocomplete)
                const POPUP_SELECTOR = '[role="dialog"], .MuiDialog-root, .MuiModal-root, .MuiPopover-root, [id*="popup"], [id*="popUp"], [id*="modal"]';
                const touchedPopup = typeof target.closest === 'function' && target.closest(POPUP_SELECTOR);
                const isChildPopup = touchedPopup && wrapperRef.current && !touchedPopup.contains(wrapperRef.current);
                ignoreNextPopupBlurRef.current = !!isChildPopup;
                if (isChildPopup) {
                    lastPopupInteractionRef.current = Date.now();
                }
            } else {
                ignoreNextPopupBlurRef.current = false;
            }
        };

        const handleDocumentTouchEnd = (event) => {
            const target = event?.target;

            if (!target) {
                return;
            }

            const touchedInsideInput = wrapperRef.current?.contains(target);
            const touchedInsidePopper =
                typeof target.closest === 'function' &&
                target.closest('.MuiAutocomplete-popper, .MuiAutocomplete-paper');

            if (touchedInsideInput || touchedInsidePopper) {
                return;
            }

            // If touch was inside a child popup, don't close
            if (ignoreNextPopupBlurRef.current) {
                return;
            }

            handleAutocompleteClose(event, 'outside-click');
        };

        const handleDocumentFocusInCapture = (event) => {
            const target = event?.target;

            if (!target) {
                return;
            }

            const isPopupInteraction = isPopupLikeElement(target);
            if (isPopupInteraction) {
                const POPUP_SELECTOR = '[role="dialog"], .MuiDialog-root, .MuiModal-root, .MuiPopover-root, [id*="popup"], [id*="popUp"], [id*="modal"]';
                const clickedPopup = typeof target.closest === 'function' && target.closest(POPUP_SELECTOR);
                const isChildPopup = clickedPopup && wrapperRef.current && !clickedPopup.contains(wrapperRef.current);
                ignoreNextPopupBlurRef.current = !!isChildPopup;
                if (isChildPopup) {
                    lastPopupInteractionRef.current = Date.now();
                }
            } else {
                ignoreNextPopupBlurRef.current = false;
            }
        };

        const handleDocumentMouseDown = (event) => {
            const target = event?.target;

            if (!target) {
                return;
            }

            const clickedInsideInput = wrapperRef.current?.contains(target);
            const clickedInsidePopper =
                typeof target.closest === 'function' &&
                target.closest('.MuiAutocomplete-popper, .MuiAutocomplete-paper');

            if (clickedInsideInput || clickedInsidePopper) {
                return;
            }

            // Don't close if the click was inside a popup/dialog/modal
            // (e.g. clicking the close button on an error dialog)
            if (isPopupLikeElement(target)) {
                return;
            }

            // Use 'outside-click' so it is not caught by the 'blur' guard in
            // handleAutocompleteClose (which blocks keyboard-originated blurs).
            handleAutocompleteClose(event, 'outside-click');
        };

        document.addEventListener('mousedown', handleDocumentMouseDownCapture, true);
        document.addEventListener('touchstart', handleDocumentTouchStartCapture, true);
        document.addEventListener('focusin', handleDocumentFocusInCapture, true);
        document.addEventListener('mousedown', handleDocumentMouseDown);
        document.addEventListener('touchend', handleDocumentTouchEnd, true);

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDownCapture, true);
            document.removeEventListener('touchstart', handleDocumentTouchStartCapture, true);
            document.removeEventListener('focusin', handleDocumentFocusInCapture, true);
            document.removeEventListener('mousedown', handleDocumentMouseDown);
            document.removeEventListener('touchend', handleDocumentTouchEnd, true);
        };
    }, [effectiveOpen, handleAutocompleteClose, isPopupLikeElement, readOnly]);

    const mergedListBoxProps = { ...(listBoxProps || {}), translate: 'no' };

    return (
        <AutocompleteDiv
            ref={wrapperRef}
            width={
                isMobileDevice
                    ? mbWidth && isPhysicianField
                        ? mbWidth
                        : ''
                    : width && isPhysicianField
                        ? width
                        : customWidth ?
                            customWidth : ''
            }
        >
            <Autocomplete 
                key={key?key:id}
                forcePopupIcon={forcePopupIcon}
                disableCloseOnSelect={disableCloseOnSelect}
                clearOnBlur={clearOnBlur}
                selectOnFocus={readOnly && false}
                {...(isSearchDropdown ? { PopperComponent: WiderPopper } : {})}
                popupIcon={<DownIcon
                    onChange={onChange}
                    disabled={disabled ?? false}
                    readOnly={readOnly}
                    value={value}
                    onClose={onClose}
                    height={'24px'} />}
                disableClearable={inputValue === '' ? true : disableClearable}
                filterOptions={
                    surgeryShipTo ? (options) => {
                        if (!options || options.length === 0) return [];
                        return options;
                    } : filterOptions
                }
                id={id}
                options={options}
                getOptionLabel={(option) =>
                    option ? option[`${field}`] || '' : ''
                }
                getOptionSelected={(option, value) =>
                    option &&
                    value &&
                    option[`${optionValue}`] === value[`${optionValue}`]
                }
                value={value === '' ? inputValue : value}
                renderOption={itemTemplate}
                getOptionDisabled={getOptionDisabled}
                onChange={(event, value, reason) => {
                    if (reason === 'select-option' && getOptionDisabled && value && getOptionDisabled(value)) {
                        return;
                    }
                    onChange(value ? value : '', reason);
                    if (
                        reason === 'select-option' &&
                        inputRef?.current &&
                        !disableCloseOnSelect
                    ) {

                        inputRef.current.blur();
                    }
                    if (reason === 'clear') {
                        handleClearAndFocus();
                    }
                }
                }
                style={style}
                classes={{
                    listbox: showScrollBar
                        ? classes.listRoot
                        : dropdownHeight
                            ? classes.dynamicHeight
                            : '',
                    option: classes.options,
                    popper: classes.popper,
                    paper: detailsMargin
                        ? classes.detailsPaper
                        : showSearchIcon
                            ? classes.paper
                            : null,
                    endAdornment: classes.endAdornment,
                    popupIndicator: classes.popupIndicator,
                    startAdornment: classes.startAdornment,
                    clearIndicator: classes.clearIndicator
                }}
                onOpen={readOnly ? undefined : onOpen}
                open={readOnly ? false : effectiveOpen} 
                onClose={handleAutocompleteClose}
                loading={loading}
                onFocus={readOnly ? undefined : onFocus}
                disableNoOptionsText={hideNoOptionText}
                loadingText={<p>{t('common.loadingText')}</p>}
                noOptionsText={noOptionsText}
                onInputChange={onInputChange}
                inputValue={inputValue}
                disabled={disabled ?? false}
                onKeyPress={onKeyPress}
                onBlur={onBlur}
                ListboxProps={mergedListBoxProps}
                renderInput={(params) => (
                    <InputField
                        params={params}
                        value={value}
                        height={height}
                        classes={classes}
                        disabled={disabled}
                        onChange={onChange}
                        onClose={onClose}
                        isMobile={isMobile}
                        Search={Search}
                        showSearchIcon={showSearchIcon}
                        inputRef={inputRef}
                        inputValue={inputValue}
                        placeholder={placeholder}
                        searchIconHeight={searchIconHeight}
                        searchIconWidth={searchIconWidth}
                        isFromShiptoAddress={isFromShiptoAddress}
                        isFromPhysician={isFromPhysician}
                        isFromSurgery={isFromSurgery}
                        isFromMazorCase={isFromMazorCase}
                        InputContainer={InputContainer}
                        changeBackgroundDisabled={changeBackgroundDisabled}
                        autoCompleteInputFieldStyle={
                            autoCompleteInputFieldStyle
                        }
                        SearchGrayInactive={SearchGrayInactive}
                        readOnly={readOnly ?? false}
                        isAddItemSearchItem={isAddItemSearchItem}
                        isProductSearch={isProductSearch}
                        isQuickOrder={isQuickOrder}
                        isAddProxy={isAddProxy}
                        loading={loading}
                    />
                )}
            />
        </AutocompleteDiv>
    );
}

AutoComplete.defaultProps = {
    isFromPhysician: true,
};
